import React, { useState } from "react";

import { InfoFillIcon, ViewEyeIcon, ViewSlashIcon, XCrossIcon } from "../../assets";
import { Loader } from "../loaders/loaders";
import { passwordRegex } from "../../utils/regex";
import {
  ACTIVATE_ACCOUNT_MESSAGES,
  PASSWORD_VALIDATION_ERROR,
} from "../../utils/message";

interface ActivateAccountModalProps {
  /** Popup heading, e.g. "Activate Customer". */
  title: string;
  /** Email the invitation was sent to — pre-filled and non-editable. */
  email: string;
  /** Disables the actions while the activation request is in flight. */
  saving?: boolean;
  onCancel: () => void;
  onSave: (password: string, confirmPassword: string) => void;
}

type PasswordField = "password" | "confirmPassword";

type FieldState = Record<PasswordField, string>;

const INITIAL_FIELDS: FieldState = { password: "", confirmPassword: "" };
const INITIAL_TOUCHED: Record<PasswordField, boolean> = {
  password: false,
  confirmPassword: false,
};
const INITIAL_VISIBILITY: Record<PasswordField, boolean> = {
  password: false,
  confirmPassword: false,
};

/**
 * Validates both password fields together, so the mismatch message clears as
 * soon as either field is corrected.
 */
const validateFields = (fields: FieldState): Partial<FieldState> => {
  const errors: Partial<FieldState> = {};

  if (!fields.password) {
    errors.password = ACTIVATE_ACCOUNT_MESSAGES.PASSWORD_REQUIRED;
  } else if (!passwordRegex.test(fields.password)) {
    errors.password = PASSWORD_VALIDATION_ERROR;
  }

  if (!fields.confirmPassword) {
    errors.confirmPassword = ACTIVATE_ACCOUNT_MESSAGES.CONFIRM_PASSWORD_REQUIRED;
  } else if (fields.password !== fields.confirmPassword) {
    errors.confirmPassword = ACTIVATE_ACCOUNT_MESSAGES.PASSWORD_MISMATCH;
  }

  return errors;
};

/**
 * ActivateAccountModal
 *
 * Sets a password for an invited account that has not completed onboarding and
 * activates it. Purely presentational — the caller owns the API call and decides
 * which listing offers this action.
 */
const ActivateAccountModal: React.FC<ActivateAccountModalProps> = ({
  title,
  email,
  saving = false,
  onCancel,
  onSave,
}) => {
  const [fields, setFields] = useState<FieldState>(INITIAL_FIELDS);
  const [touched, setTouched] = useState(INITIAL_TOUCHED);
  const [passwordVisible, setPasswordVisible] = useState(INITIAL_VISIBILITY);

  const errors = validateFields(fields);
  const isFormValid = Object.keys(errors).length === 0;

  const handleFieldChange = (field: PasswordField, value: string) => {
    setFields((prev) => ({ ...prev, [field]: value }));
  };

  const handleBlur = (field: PasswordField) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const toggleVisibility = (field: PasswordField) => {
    setPasswordVisible((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSave = () => {
    if (!isFormValid || saving) return;
    onSave(fields.password, fields.confirmPassword);
  };

  /** Renders a password field with its show/hide toggle and validation message. */
  const renderPasswordField = (
    field: PasswordField,
    label: string,
    placeholder: string
  ) => (
    <div className="formRow">
      <div className="formCol">
        <div className="form__Field">
          <label htmlFor={field} className="form__label">
            {label} <span className="mandatory-mark">*</span>
          </label>
          <div className="form__paswordfield">
            <input
              id={field}
              type={passwordVisible[field] ? "text" : "password"}
              className="form__input form__input--sm"
              placeholder={placeholder}
              value={fields[field]}
              autoComplete="new-password"
              onChange={(e) => handleFieldChange(field, e.target.value)}
              onBlur={() => handleBlur(field)}
            />
            <button
              type="button"
              className="btn"
              onClick={() => toggleVisibility(field)}
            >
              {passwordVisible[field] ? <ViewEyeIcon /> : <ViewSlashIcon />}
            </button>
          </div>
          {touched[field] && errors[field] && (
            <span className="form__error">{errors[field]}</span>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="modal modal--hazmatDetail modal--open">
        <div className="modal__dialog">
          <div className="modal__content modal__content--md">
            <div className="modal__header">
              <h4 className="modal__header--title">{title}</h4>
              <button
                className="btn btn__transparent btnClose"
                onClick={onCancel}
                disabled={saving}
              >
                <XCrossIcon />
              </button>
            </div>
            <div className="modal__body">
              <div className="formRow">
                <div className="formCol">
                  <div className="form__Field">
                    <label htmlFor="activateEmail" className="form__label">
                      Email
                    </label>
                    <input
                      id="activateEmail"
                      type="text"
                      className="form__input form__input--sm form__input--disabled"
                      value={email}
                      disabled
                    />
                  </div>
                </div>
              </div>
              {renderPasswordField("password", "Password", "Enter Password")}
              <div className="modal__note">
                <span className="modal__note__message">
                  <span className="modal__note__icon">
                    <InfoFillIcon />
                  </span>
                  {ACTIVATE_ACCOUNT_MESSAGES.PASSWORD_RULE_HINT}
                </span>
              </div>
              {renderPasswordField(
                "confirmPassword",
                "Confirm Password",
                "Re-Enter Password"
              )}
            </div>
            <div className="modal__footer">
              <div className="btn__group">
                <button
                  className="btn btn__white"
                  onClick={onCancel}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  className="btn btn__primary"
                  disabled={!isFormValid || saving}
                  onClick={handleSave}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="modal__backdrop"></div>
      </div>
      {/* Overlays the popup while the activation request is in flight; the
          popup stays mounted so the entered values survive a failure. */}
      {saving && <Loader />}
    </>
  );
};

export default ActivateAccountModal;
